// Normalizes the three things the index lists — Views (dashboards), Samples (audit_reports), and
// Reports (report builder) — into the one shape ItemCard and the list rows render.
//
// Naming trap this file exists to contain: "report" means three different records in this codebase.
// View = Dashboard, Sample = AuditReport, Report = Report. The `apiType` below is the wire value
// folders#move_item expects and is the only place those names should appear.
import { faTable, faFlask, faFileAlt, faChartBar, faCheck, faUser, faSitemap } from '@fortawesome/free-solid-svg-icons'
import { viewPath } from 'helpers/utils'

export const ITEM_TYPES = {
  view: { key: 'view', apiType: 'dashboard', icon: faTable, label: 'View' },
  sample: { key: 'sample', apiType: 'audit_report', icon: faFlask, label: 'Sample' },
  report: { key: 'report', apiType: 'report', icon: faFileAlt, label: 'Report' }
}

// Drag ids are `<prefix>-<uuid>`; the prefix maps back to a type when a drop lands.
const DRAG_PREFIX = { view: 'dashboard', sample: 'sample', report: 'report' }

export const dragIdFor = (type, id) => `${DRAG_PREFIX[type]}-${id}`

export const parseDragId = (dragId) => {
  const type = Object.keys(DRAG_PREFIX).find((t) => dragId.startsWith(`${DRAG_PREFIX[t]}-`))
  if (!type) return null
  return { type, id: dragId.slice(DRAG_PREFIX[type].length + 1) }
}

// folders#move_item speaks apiType; drag ids and ITEM_TYPES are keyed by the UI type. This maps back.
export const typeKeyForApiType = (apiType) =>
  Object.keys(ITEM_TYPES).find((t) => ITEM_TYPES[t].apiType === apiType)

// Sections carry a display `unit` that differs from the raw name; fall back to the name.
const sectionLabel = (sections, sectionName) => {
  if (!sectionName) return ''
  const section = sections?.find((s) => s.name === sectionName)
  return section?.unit || sectionName
}

const sharedMeta = (item) =>
  item.shared_users?.length > 0
    ? [{ icon: faUser, label: item.shared_users.length, title: 'Shared with' }]
    : []

export const toViewItem = (dashboard, { sections } = {}) => ({
  type: 'view',
  id: dashboard.id,
  dragId: dragIdFor('view', dashboard.id),
  name: dashboard.name,
  description: dashboard.description,
  userId: dashboard.user_id,
  userFullName: dashboard.user_full_name,
  updatedAt: dashboard.updated_at,
  sharedUsers: dashboard.shared_users || [],
  scheduledEmails: dashboard.scheduled_emails || [],
  sectionName: dashboard.section_name,
  sectionIds: [dashboard.section_id].filter(Boolean),
  sectionLabel: sectionLabel(sections, dashboard.section_name),
  path: viewPath(dashboard.section_name, dashboard.id),
  meta: [
    { icon: faFlask, label: dashboard.audit_reports_count || 0, title: 'Samples' },
    ...sharedMeta(dashboard)
  ],
  raw: dashboard
})

export const toSampleItem = (sample, { sections } = {}) => ({
  type: 'sample',
  id: sample.id,
  dragId: dragIdFor('sample', sample.id),
  name: sample.name,
  description: null,
  userId: sample.user_id,
  userFullName: sample.owner ? `${sample.owner.first_name} ${sample.owner.last_name}` : '',
  updatedAt: sample.updated_at,
  sharedUsers: sample.shared_users || [],
  scheduledEmails: [],
  sectionName: sample.section?.name || '',
  sectionIds: [sample.section_id].filter(Boolean),
  sectionLabel: sectionLabel(sections, sample.section?.name),
  path: `/samples/${sample.id}`,
  completeCount: sample.complete_count || 0,
  totalCount: sample.total_count || 0,
  // A sample not attached to a view is worth flagging — the index has always shown this.
  dashboard: sample.dashboard,
  meta: [
    { icon: faCheck, label: `${sample.complete_count || 0}/${sample.total_count || 0}`, title: 'Reviewed' },
    ...sharedMeta(sample)
  ],
  raw: sample
})

// 'template' once a report has been split, 'child' for one of the results, null for an ordinary
// report that is neither.
const reportSplitRole = (report) => {
  if (report.is_child) return 'child'
  return report.child_reports_count ? 'template' : null
}

// Which side of a split a report is on replaces the type word in the card's type badge rather than
// adding a second badge beside it: it is a kind of report, not a property hung off one.
const SPLIT_ROLE_LABELS = { child: 'Child report', template: 'Template' }
const reportTypeLabel = (report) => SPLIT_ROLE_LABELS[reportSplitRole(report)] || ITEM_TYPES.report.label

export const toReportItem = (report) => ({
  type: 'report',
  id: report.id,
  dragId: dragIdFor('report', report.id),
  name: report.name,
  description: report.description,
  userId: report.user_id,
  userFullName: report.user_full_name,
  updatedAt: report.updated_at,
  sharedUsers: report.shared_users || [],
  scheduledEmails: report.scheduled_emails || [],
  sectionName: '',
  // A report spans datasets rather than belonging to one section, so it matches a section filter
  // if any dataset does. Datasets without a section contribute nothing here — see
  // matchesSectionFilter, which lets a report with no sections at all through rather than
  // filtering it on a dimension it has no data for.
  sectionIds: (report.datasets || []).map((d) => d.section_id).filter(Boolean),
  sectionLabel: '',
  path: `/reports/${report.id}`,
  // Which side of a split this report sits on, rendered as a badge on the card rather than a meta
  // chip: it changes what the report *is*, not how much is in it.
  splitRole: reportSplitRole(report),
  typeLabel: reportTypeLabel(report),
  childReportsCount: report.child_reports_count || 0,
  meta: [
    { icon: faTable, label: report.datasets?.length || 0, title: 'Datasets' },
    { icon: faChartBar, label: report.charts_count || 0, title: 'Components' },
    // How many children follow a template. A count belongs with the other counts, not in the badge.
    ...(report.child_reports_count
      ? [{ icon: faSitemap, label: report.child_reports_count, title: 'Child reports' }]
      : []),
    ...sharedMeta(report)
  ],
  raw: report
})

// A module chip selects one or more section ids. Views and samples match on their own section;
// reports match if any dataset does. An item with no sections is never filtered out — it has no
// value on this dimension, and hiding it would make reports vanish the moment a chip is clicked.
export const matchesSectionFilter = (item, selectedSectionIds) => {
  if (!selectedSectionIds?.length) return true
  if (!item.sectionIds.length) return true
  return item.sectionIds.some((id) => selectedSectionIds.includes(id))
}
